import { PublicKey, TransactionInstruction } from "@solana/web3.js";
import { createNoopSigner, publicKey, none, signerIdentity } from "@metaplex-foundation/umi";
import { createUmi } from "@metaplex-foundation/umi-bundle-defaults";
import {
  createMetadataAccountV3,
  fetchMetadata,
  findMetadataPda,
  mplTokenMetadata,
} from "@metaplex-foundation/mpl-token-metadata";
import { toWeb3JsInstruction } from "@metaplex-foundation/umi-web3js-adapters";

export const TOKEN_METADATA_PROGRAM = new PublicKey(
  "metaqbxxUerdq28cj1RbAWkYQm3ybzjb6a8bt518x1s"
);

export function metadataPdaFor(mint: PublicKey): PublicKey {
  const [pda] = PublicKey.findProgramAddressSync(
    [Buffer.from("metadata"), TOKEN_METADATA_PROGRAM.toBuffer(), mint.toBuffer()],
    TOKEN_METADATA_PROGRAM
  );
  return pda;
}

export interface MetadataInput {
  name: string;
  symbol: string;
  uri: string;
  sellerFeeBasisPoints: number;
  mutable: boolean;
}

/**
 * Builds the CreateMetadataAccountV3 instruction without letting Umi own the
 * signing. The wallet signs through this app's single send path, so every
 * transaction gets the same simulate-then-approve treatment.
 *
 * Order matters: this needs the mint authority as a signer, so it MUST run
 * before mint authority is revoked. Revoking first strands the token without
 * metadata permanently.
 */
export function buildCreateMetadataIx(
  rpcUrl: string,
  mint: PublicKey,
  payer: PublicKey,
  data: MetadataInput
): TransactionInstruction[] {
  const umi = createUmi(rpcUrl).use(mplTokenMetadata());
  const signer = createNoopSigner(publicKey(payer.toBase58()));
  umi.use(signerIdentity(signer));

  const builder = createMetadataAccountV3(umi, {
    metadata: findMetadataPda(umi, { mint: publicKey(mint.toBase58()) }),
    mint: publicKey(mint.toBase58()),
    mintAuthority: signer,
    payer: signer,
    updateAuthority: signer.publicKey,
    data: {
      name: data.name,
      symbol: data.symbol,
      uri: data.uri,
      sellerFeeBasisPoints: data.sellerFeeBasisPoints,
      creators: none(),
      collection: none(),
      uses: none(),
    },
    isMutable: data.mutable,
    collectionDetails: none(),
  });

  return builder.getInstructions().map((ix) => toWeb3JsInstruction(ix));
}

export interface MetadataFacts {
  address: string;
  name: string;
  symbol: string;
  uri: string;
  isMutable: boolean;
  updateAuthority: string;
}

export async function readMetadata(
  rpcUrl: string,
  mint: PublicKey
): Promise<MetadataFacts | null> {
  const umi = createUmi(rpcUrl).use(mplTokenMetadata());
  const pda = findMetadataPda(umi, { mint: publicKey(mint.toBase58()) });
  try {
    const md = await fetchMetadata(umi, pda, { commitment: "confirmed" });
    return {
      address: pda[0].toString(),
      // Metaplex NUL-pads these fields on-chain; trim before comparing.
      name: md.name.replace(/\0+$/, ""),
      symbol: md.symbol.replace(/\0+$/, ""),
      uri: md.uri.replace(/\0+$/, ""),
      isMutable: md.isMutable,
      updateAuthority: md.updateAuthority.toString(),
    };
  } catch {
    return null;
  }
}

// Pure, and tested on its own; see uri-probe.ts for why it lives apart.
export { probeMetadataUri } from "./uri-probe";
